import { useEffect, useRef } from 'react';
import { useMapStore } from '@/stores/mapStore';
import { useSharedLocationStore } from '@/components/share/sharedLocationStore';
import {
  parseShareLocation,
  formatViewHash,
  DEFAULT_PIN_ZOOM,
} from '@/components/share/shareUrl';

const HASH_SYNC_DEBOUNCE_MS = 400;

/**
 * Wires up shareable-location URLs on the web app:
 *
 *  1. ON LOAD (once, after the map is ready): if the URL contains a location —
 *     a `?ll=<lat>,<lng>` pin and/or a `#map=<zoom>/<lat>/<lng>` view — jump the
 *     map there. For a `?ll=` pin it also records the pin so a marker drops and
 *     the shared-location card opens.
 *
 *  2. ON MOVE: keeps the `#map=...` hash in sync with the viewport (debounced on
 *     moveend/zoomend, via history.replaceState so we don't spam back-history),
 *     so the address bar is always a shareable view — OSM-style.
 *
 * Call once from a component that lives under the map (e.g. inside
 * MapContainer). Reads the map instance straight from the store, so it needs no
 * props.
 */
export function useShareableLocation(): void {
  const map = useMapStore((s) => s.map);
  const isLoaded = useMapStore((s) => s.isLoaded);
  const setPin = useSharedLocationStore((s) => s.setPin);
  const restoredRef = useRef(false);

  // 1) On-load restore — runs exactly once, after the map has loaded.
  useEffect(() => {
    if (!map || !isLoaded || restoredRef.current) return;
    restoredRef.current = true;

    const { view, pin } = parseShareLocation(window.location.hash, window.location.search);
    const target = pin ?? view?.center ?? null;
    if (!target) return;

    const zoom = view?.zoom ?? (pin ? DEFAULT_PIN_ZOOM : map.getZoom());
    // Instant jump on first paint (no fly animation on cold load).
    map.jumpTo({ center: [target.lng, target.lat], zoom });
    // Keep the store viewport aligned so other UI reads the right values.
    useMapStore.getState().setViewport({ center: target, zoom });

    if (pin) setPin(pin);
  }, [map, isLoaded, setPin]);

  // 2) Keep the hash in sync with the viewport as the user pans/zooms.
  useEffect(() => {
    if (!map) return;

    let timer: ReturnType<typeof setTimeout> | undefined;
    const sync = () => {
      if (timer) clearTimeout(timer);
      timer = setTimeout(() => {
        const center = map.getCenter();
        const hash = formatViewHash(map.getZoom(), { lat: center.lat, lng: center.lng });
        // Preserve any existing query (e.g. a `?ll=` pin) — only replace the hash.
        const url = `${window.location.pathname}${window.location.search}${hash}`;
        window.history.replaceState(window.history.state, '', url);
      }, HASH_SYNC_DEBOUNCE_MS);
    };

    map.on('moveend', sync);
    map.on('zoomend', sync);
    return () => {
      if (timer) clearTimeout(timer);
      map.off('moveend', sync);
      map.off('zoomend', sync);
    };
  }, [map]);
}
